import api from './api';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { DEFAULT_SIGNUP_ENTITY, DEFAULT_SIGNUP_AGENT_ID } from '../config/signupDefaults';

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

const DEFAULT_ENTITY = DEFAULT_SIGNUP_ENTITY;
const DEFAULT_AGENT_ID = DEFAULT_SIGNUP_AGENT_ID;

/** Unified CRM entry for individual + corporate lead step-1 (corporate delegates on server). */
const NEW_LEAD_REGISTRATION_PATH = '/v1/crm/customers/newleadRegistration';

const hasMeaningfulAgentId = (value) =>
    value !== undefined && value !== null && String(value).trim() !== '';

const withDefaultAgentId = (params) => {
    if (params == null) {
        return { agentId: DEFAULT_AGENT_ID };
    }
    if (typeof FormData !== 'undefined' && params instanceof FormData) {
        const current = params.get('agentId');
        if (!hasMeaningfulAgentId(current)) {
            if (current !== null) params.delete('agentId');
            params.append('agentId', DEFAULT_AGENT_ID);
        }
        return params;
    }
    if (typeof params !== 'object') {
        return params;
    }
    if (hasMeaningfulAgentId(params.agentId)) {
        return params;
    }
    return { ...params, agentId: DEFAULT_AGENT_ID };
};

const withSignupDefaults = (params) =>
    withDefaultEntity(withDefaultAgentId(params));

/**
 * Always set CRM `entity` to DEFAULT_ENTITY (GODOFS — matches backend).
 * URL/query “regulator” labels (FSC, FSA, SCA…) stay in the UI only; they must not override API entity.
 */
const withDefaultEntity = (params) => {
    if (params == null) {
        return { entity: DEFAULT_ENTITY };
    }
    if (typeof FormData !== 'undefined' && params instanceof FormData) {
        params.delete('entity');
        params.append('entity', DEFAULT_ENTITY);
        return params;
    }
    if (typeof params !== 'object') {
        return params;
    }
    return { ...params, entity: DEFAULT_ENTITY };
};

const encryptData = (data, key) => {
return CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
};
// const email = 'nq@godofx.com';
// const password = 'Godo@702';

export const authorization = () => {
    const encryptedData = encryptData({
        email: process.env.REACT_APP_USER,
        password: process.env.REACT_APP_PASSWORD,
    }, SECRET_KEY);
    return api.post('/v1/crm/auth/authLogin', { data: encryptedData });
};

export const authorizationCp = (email, password) => {
    return api.post('/v1/cp/auth/login', {
        email,
        password,
    });
};

export const postClient = (params) => {
    return api.post(NEW_LEAD_REGISTRATION_PATH, withSignupDefaults(params));
};

export const postLead = (params) => {
    return api.post(NEW_LEAD_REGISTRATION_PATH, withSignupDefaults(params));
};

export const updateClient = (id, params, rec) => {
    return api.patch('/v1/crm/customers/registrationUpdate/' + id, withDefaultEntity(params));
};

export const updateLeadInfo = (token) => {
    if (token == null || String(token).trim() === '') {
        return Promise.reject(new Error('Missing verification token'));
    }
    const safeToken = encodeURIComponent(String(token).trim());
    return api.patch(`/v1/crm/customers/leadUpdate/${safeToken}`);
};

export const postCorprateLeadInfo = (params) => {
    console.log('token is ', params);
    return api.post(NEW_LEAD_REGISTRATION_PATH, withSignupDefaults(params));
};

export const postDirectorInfo = (params) => {
    console.log('Director is ', params);
    return api.post(`/v1/crm/customers/registrationDirector`, withSignupDefaults(params));
};

export const postBeneficialOwnerInfo = (params) => {
    console.log('beneficialOwner is ', params);
    return api.post(`/v1/crm/customers/registrationBenefictialOwners`, withSignupDefaults(params));
};

export const postauthorizedSignatoryInfo = (params) => {
    console.log('authorizedSignatory is ', params);
    return api.post(`/v1/crm/customers/registrationauthorizedSignatory`, withSignupDefaults(params));
};

export const postCorporateInfoUpdate = (id, params) => {
    console.log('CorporateInfoUpdate is ', params);
    return api.patch(`/v1/crm/customers/corprateInfoUpdate/${id}`, withDefaultEntity(params));
};

/** Default instance sets Content-Type: application/json; axios would then JSON-stringify FormData. */
const formDataTransformRequest = [
    (payload, headers) => {
        if (typeof FormData !== 'undefined' && payload instanceof FormData && headers) {
            if (typeof headers.delete === 'function') {
                headers.delete('Content-Type');
            } else if (headers.common) {
                delete headers.common?.['Content-Type'];
            }
        }
        return payload;
    },
    ...(Array.isArray(axios.defaults.transformRequest)
        ? axios.defaults.transformRequest
        : axios.defaults.transformRequest
            ? [axios.defaults.transformRequest]
            : []),
];

export const uploadDocuments = async (params) => {
    console.log('Upload Doc Files', params);
    return api.post('/v1/crm/documents', withDefaultEntity(params), {
        transformRequest: formDataTransformRequest,
    });
};

export const singleRegistration = async (params) => {
    return api.post('/v1/crm/customers/singleRegistration', withSignupDefaults(params));
}

export const gtnRegistration = async (params) => {
    return api.post('/v1/gtn/account/investorAccount', withSignupDefaults(params));
}

export const emailExist = async (params) => {
    const type = encodeURIComponent(String(params.type || "email"));
    const value = encodeURIComponent(String(params.value || "").trim());
    return api.get(
        `/v1/crm/customers/emailExist?${type}=${value}&isClient=1`
    );
};

export const emailAndPhoneExist = async (params) => {
    return api.get(`/v1/crm/customers/emailExist?email=${params.email}&phone=${params.phone}&isClient=1`);
}

export const getIbCreditStatus = async (customerId, opts = {}) => {
    const id = encodeURIComponent(String(customerId).trim());
    const qs = new URLSearchParams();
    if (opts.rm != null && String(opts.rm).trim() !== "") {
        qs.set("rm", String(opts.rm).trim());
    }
    if (opts.agentId != null && String(opts.agentId).trim() !== "") {
        qs.set("agentId", String(opts.agentId).trim());
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const path = `/v1/crm/customers/ib-credit-status/${id}${suffix}`;
    if (process.env.NODE_ENV === "development") {
        console.log("[getIbCreditStatus] GET", path);
    }
    const out = await api.get(path);
    if (
        out &&
        typeof out === "object" &&
        ("isIbCredit" in out || "creditisib" in out)
    ) {
        return out;
    }
    if (out?.data?.result != null) {
        return out.data.result;
    }
    return out;
};
